import {html, LitElement, TemplateResult, unsafeCSS} from 'lit';
import {property} from 'lit/decorators/property.js';
import {customElement} from 'lit/decorators/custom-element.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import {
    Pagination,
    PaginationInterface
} from './pagination.ts';
import {
    FilterGroupInterface,
    Filters,
    FiltersInterface
} from '../../library/class/filters.class.ts';
import cardListCSS from './bg-document-list.scss?inline';
import {SlCard} from '@shoelace-style/shoelace';
import {getBlockAlignment} from '../../library/design-helpers.ts';
import {classMap} from 'lit/directives/class-map.js';
import {BgDocumentCardInterface, BgTagInterface} from "../bg-document-card/bg-document-card.ts";
import "../bg-document-card/bg-document-card.ts";
import {unsafeHTML} from "lit/directives/unsafe-html.js";
import {state} from "lit/decorators/state.js";

// Important! "shoelaceElementFactory" is used to announce the use of Shoelace components
// @ts-ignore
const shoelaceElementFactory = (tagName: string) => {
    // Define a list of all Shoelace components being used
    customElements.define(tagName, SlCard);
};

export interface BgDocumentListInterface {
    pagination?: PaginationInterface;
    filters?: FiltersInterface;
    blockAlignment?: string;
    siteIdentifier?: string;
    pageSize?: number;
    colorScheme?: string; /* used by storybook only */
    cardStyle?: string; /* used by storybook only */
}

/**
 * BgDocumentList
 * Pagination and filtering are included, however they are not responsible for providing the necessary page content of cards,
 * instead the pagination and filtering will change the current URL to the selected page
 * It is the responsibility of the back-office or parent component to take care of presenting the required page content.
 * @slot - This element has only a default slot, this should be used to provide card contents.
 */
@customElement('bg-document-list')
export class BgDocumentList extends LitElement {
    @property()
    docsHint = '';

    @property({type: Object})
    pagination?: PaginationInterface = undefined;

    @property({type: Object})
    filters?: FiltersInterface = undefined;

    @property({type: String, attribute: 'block-alignment'})
    blockAlignment?: string;

    @property({type: String, attribute: 'site-identifier'})
    siteIdentifier?: string = '';

    @property({type: Number, attribute: 'page-size'})
    pageSize: number = 10;

    constructor(args: BgDocumentListInterface) {
        super();
        if (!args) {
            return;
        }
        this.pagination = args.pagination;
        this.filters = args.filters;
        this.pageSize = args.pageSize ? args.pageSize : 10;
    }

    private sortDescLabel = 'Date (Newest first)';
    private sortAscLabel = 'Date (Oldest first)';
    @state()
    private _pagination?: Pagination;
    @state()
    private _filters?: Filters;
    @state()
    private responseData: BgDocumentCardInterface[] = [];
    @state()
    private documentCards: string = '';
    @state()
    private infoBox: string = '';
    @state()
    private availableFilters: string[] = [];
    @state()
    private infoButtons: string = '';
    @state()
    private currentSortOption: string = 'desc';
    @state()
    private filterList: string[] = [];
    @state()
    private totalResults: number = 0;
    @state()
    private currentSortLabel: string = '';
    @state()
    private noFilterSelected: string = '';
    @state()
    private renderedPagination: string | TemplateResult<1> = '';
    @state()
    private documents: any;

    connectedCallback() {
        super.connectedCallback()
        this.paginationClick().then();
    }

    render = () => {
        const filtersURL = getFiltersFromUrl();
        this._pagination = new Pagination(this.pagination, filtersURL);
        this._filters = new Filters(this.filters);
        const filters = this._filters.renderFilters();
        this.renderedPagination = this._pagination?.renderPagination() || '';
        const classMapping = {
            'bg-document-list': true,
            ...getBlockAlignment(this.blockAlignment || ''),
        };

        return html`
            <slot class="bg-document-list-content" name="content"></slot>
            ${filters}
            <slot name="results-info"></slot>
            ${unsafeHTML(this.infoButtons)}
            ${unsafeHTML(this.infoBox)}
            <div class="${classMap(classMapping)}" part="cards">
                <slot></slot>
                ${unsafeHTML(this.documentCards)}
            </div>
            ${unsafeHTML(this.noFilterSelected)}

            ${this.renderedPagination}
            <style>
                .bg-document-list {
                    color: var(--bg-color-foreground);
                    ${this.style}
                }
            </style>
        `;
    };

    static styles = unsafeCSS(cardListCSS);

    firstUpdated = async () => {
        await this.waitForSlots()
    };


    async waitForSlots() {
        //Slots are loading after update functions run. This one is for waiting until slots are loaded.
        let retries = 5;
        while ((this.responseData?.length > 0) && retries > 0) {
            await new Promise((resolve) => setTimeout(resolve, 100));
            retries--;
        }

        var currentFilterLabels = this.filters?.currentFilterLabels || [];
        this.documents = await this.getDocuments(currentFilterLabels);
        this.availableFilters = await this.documents.availableFilters || [];

        await this.updateDocumentCardList(currentFilterLabels, this.documents);
        await this.filterButtonClick();
        await this.updatePagination(this.filters?.currentFilterLabels?.length || 0);

        await this.updateDocumentCards();
        await this.updateInfoBoxes();
        await this.updateDocumentInfoButtons();
        await this.applySortingEvent();
        await this.updateResultText();
        await this.renderNoFilterSelected();
        await this.handleAvailableFiltersOnStart();

    }

    /* End manage event when selecting a value from the dropdown */
    private async renderNoFilterSelected() {
        if (this.totalResults === 0 && this.filterList.length === 0) {
            this.noFilterSelected = `
        <div class="bg-advanced-filter-no-result">
            Your document results will appear here.
            </div>
            `
        } else {
            this.noFilterSelected = ``
        }
    }


    private paginationEventAdded = false;
    
    private async paginationClick() {
        if (this.paginationEventAdded) {
            return;
        }

        const paginationContainer = this.shadowRoot?.querySelector('.bg-pagination-container');
        if (!paginationContainer) {
            return;
        }

        paginationContainer.addEventListener('click', async (event) => {
            const buttonElement = (event.target as HTMLElement).closest('.bg-document-list-page');

            if (!buttonElement || buttonElement.hasAttribute('disabled')) {
                return;
            }

            const page = buttonElement.getAttribute('page');
            if (!page) {
                return;
            }

            const activePageElement = this.shadowRoot?.querySelector('.bg-document-list-page.active');
            const activePage = activePageElement ? parseInt(activePageElement.getAttribute('page') || '0') : 0;
            let targetPage: number;

            if (page === 'next') {
                if (this.pagination === undefined || this.pagination.totalPages === 0) {
                    targetPage = 1;
                } else {
                    targetPage = activePage + 1 > this.pagination.totalPages ? this.pagination?.totalPages : activePage + 1;
                }
            } else if (page === 'previous') {
                targetPage = activePage - 1 < 1 ? 1 : activePage - 1;
            } else {
                targetPage = parseInt(page);
            }

            if (this.pagination) {
                this.pagination.page = targetPage;
                this.setSingleUrlParameter('page', targetPage.toString());
                this.filterList = await this.setFilterList();
                this.documents = await this.getDocuments(this.filterList);
                await this.updateDocumentCardList(this.filterList, this.documents);
                await this.updateDocumentCards();
                await this.updateResultText();
            }

            this.updatePaginationButtons(targetPage);
        });

        this.paginationEventAdded = true;
        this.updatePaginationButtons(this.pagination?.page || 1);
    }

    private updatePaginationButtons(targetPage: number) {
        const allButtons = this.shadowRoot?.querySelectorAll('.bg-document-list-page');
        if (!allButtons) return;

        const totalPages = this.pagination?.totalPages || 1;

        for (const btn of allButtons) {
            btn.classList.remove('active');
            btn.removeAttribute('disabled');

            const pageAttr = btn.getAttribute('page');
            if (pageAttr && parseInt(pageAttr) === targetPage) {
                btn.classList.add('active');
                btn.setAttribute('disabled', 'true');
            }
        }

        const prevButton = this.shadowRoot?.querySelector('.bg-document-list-page[page="previous"]');
        const nextButton = this.shadowRoot?.querySelector('.bg-document-list-page[page="next"]');

        if (prevButton) {
            if (targetPage === 1) {
                prevButton.setAttribute('disabled', 'true');
            }
        }

        if (nextButton) {
            if (targetPage === totalPages) {
                nextButton.setAttribute('disabled', 'true');
            }
        }
    }

    private async handleAvailableFiltersOnStart() {
        if (!this.availableFilters || this.availableFilters.length === 0) {
            return;
        }
        const advancedFilterButtons = this.shadowRoot?.querySelectorAll('.bg-advanced-filter-option') ?? [];
        advancedFilterButtons.forEach(button => {
            const filter = button.getAttribute('filter')?.toLowerCase() || '';
            if (!this.availableFilters.includes(filter)) {
                if (!button.classList.contains('hidden')) {
                    button.classList.add('hidden');
                }
            }
        });
    }

    private async filterButtonClick() {
        const advancedFilterButtons = this.shadowRoot?.querySelectorAll('.bg-advanced-filter-option') ?? [];
        advancedFilterButtons.forEach(button => {
            button.addEventListener('click', async () => {
                // Reset pagination to the first page on filter change
                if (this.pagination) {
                    this.pagination.page = 1;
                }
                this.setSingleUrlParameter('page', '');


                const buttonGroupIndex = parseInt(button.getAttribute('filter-group'));
                const nextGroupIndex = buttonGroupIndex + 1;
                const filterGroups = this.shadowRoot?.querySelectorAll('.bg-document-list-multiple-filter') || [];
                const currentGroup = filterGroups[buttonGroupIndex];
                const nextGroup = filterGroups[nextGroupIndex];
                const pattern = /\{[^{}]+\}/g;
                let groupLabel = this.shadowRoot?.querySelectorAll('.bg-document-library-filter-group-label');

                button.classList.toggle('active');
                
                if (button.classList.contains('active')) {
                    currentGroup?.classList.add('active-group-row');
                    Array.from(advancedFilterButtons)
                        .filter(btn => btn !== button && btn.getAttribute('filter-group') === button.getAttribute('filter-group'))
                        .forEach(btn => {
                            btn.classList.remove('active');
                            btn.classList.add('hidden');
                        })
                    
                    this.filterList = await this.setFilterList();
                    this.setSingleUrlParameter('filter', decodeURIComponent(this.filterList.toString()) || '');
                    this.documents = await this.getDocuments(this.filterList);
                    this.availableFilters = await this.documents.availableFilters || [];
                    
                    const nextGroupFilters = Array.from(nextGroup?.querySelectorAll('.bg-advanced-filter-option') || [])
                        .filter(btn => btn.getAttribute('filter-group') === nextGroupIndex.toString());
                    const nextGroupAvailableFilters = nextGroupFilters
                        .filter(nextBtn => this.availableFilters.includes(nextBtn.getAttribute('filter')?.toLowerCase() || ''));

                    if (nextGroupAvailableFilters.length > 0) {
                        nextGroup?.classList.remove('hidden');
                        nextGroupFilters
                            .forEach(nextBtn => {
                                if (!nextBtn.classList.contains('hidden')) {
                                    nextBtn.classList.add('hidden');
                                }
                                if (this.availableFilters.includes(nextBtn.getAttribute('filter')?.toLowerCase() || '')) {
                                    nextBtn.classList.remove('hidden');
                                }
                            });
                    }
                    if(groupLabel && groupLabel.length > buttonGroupIndex) {
                        let tagPrefix = button.getAttribute("prefix") || "";
                        groupLabel[buttonGroupIndex].textContent = this.filters?.filterGroup[buttonGroupIndex]?.filterLabel.replace(pattern, tagPrefix) || '';
                    }
                } else {
                    filterGroups.forEach((group, index) => {
                        if (index > buttonGroupIndex) {
                            var groupButtons: Element[] = Array.from(group.querySelectorAll('.bg-advanced-filter-option'));
                            groupButtons
                                .filter(btn => btn.getAttribute('filter-group') === index.toString())
                                .forEach(nextBtn => {
                                    if (nextBtn.classList.contains('active')) {
                                        nextBtn.classList.remove('active');
                                    }
                                    if (!nextBtn.classList.contains('hidden')) {
                                        nextBtn.classList.add('hidden');
                                    }
                                });
                            if (!group?.classList.contains('hidden')) {
                                group?.classList.add('hidden');
                                group?.classList.remove('active-group-row');
                            }
                        }
                        if(groupLabel && groupLabel.length > buttonGroupIndex) {
                            groupLabel[buttonGroupIndex].textContent = this.filters?.filterGroup[buttonGroupIndex]?.filterLabel.replace(pattern, "") || '';
                        }
                    })
                    this.filterList = await this.setFilterList();
                    this.setSingleUrlParameter('filter', decodeURIComponent(this.filterList.toString()) || '');
                    this.documents = await this.getDocuments(this.filterList);
                    this.availableFilters = await this.documents.availableFilters || [];
                    
                    currentGroup?.classList.remove('active-group-row');
                    Array.from(advancedFilterButtons)
                        .filter(btn => btn !== button && btn.getAttribute('filter-group') === button.getAttribute('filter-group'))
                        .forEach(btn => {
                            if (this.availableFilters.includes(btn.getAttribute('filter')?.toLowerCase() || '')) {
                                btn.classList.remove('hidden');
                            }
                        })
                }
                await this.updatePageResults()
                await this.updateResultText()
            })
        })
    }

    private async setFilterList() {
        const activeButtons = this.shadowRoot?.querySelectorAll('.bg-advanced-filter-option.active') ?? [];
        let filterList: string[] = [];
        activeButtons.forEach(activeButton => {
            filterList.push(activeButton.getAttribute("filter").toLowerCase())
        })
        return filterList;
    }

    private async updatePageResults() {
        await this.updateDocumentCardList(this.filterList, this.documents);
        await this.updateDocumentCards();
        await this.updateInfoBoxes()
        await this.renderNoFilterSelected()
        await this.updatePagination(this.filterList.length || 0);
    }

    private async updateDocumentCardList(tagValues: string[] | [], data: any) {

        this.responseData = tagValues.length > 0 ? data.searchResults || [] : [];
        this.totalResults = tagValues.length > 0 ? data.totalHits : 0 || 0;
        this.availableFilters = await data.availableFilters;
        if (tagValues.length > 0) {
            this.pagination = {
                page: data.currentPageNumber || 1,
                totalPages: await data.totalPages || 1,
                pageHref: this.pagination?.pageHref || '?page=',
            }
        } else {
            this.pagination = {
                page: 0,
                totalPages: 0,
                pageHref: this.pagination?.pageHref || '?page=',
            }
        }
    }

    private async getDocuments(tagValues?: string[] | []) {
        try {
            const url = '/search/documents/';
            const body = {
                "tagName": "tags",
                "tagValues": tagValues,
                "siteIdentifier": this.siteIdentifier,
                "doctypeFilter": [
                    "ecDocument"
                ],
                "OrderByField": "date",
                "SortDirection": this.currentSortOption,
                "booleanOperation": "and",
                "maxResultsToRetrieve": 1000,
                "pageResults": true,
                "pageIndexNumber": this.pagination?.page || 1,
                "skip": 0,
                "resultsPerPage": this.pageSize
            };

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body)
            })

            if (!response.ok) {
                throw new Error(`Response status: ${response.status}`);
            }
            return await response.json();
        } catch (error: any) {
            return {
                "searchResults": [],
                "totalHits": 0,
                "currentPageNumber": 1,
                "totalPages": 0,
                "query": "",
                "availableFilters": []
            }
        }
    }

    private async updatePagination(filterCount: number) {
        if (filterCount > 0) {
            const filtersURL = getFiltersFromUrl();
            this._pagination = new Pagination(this.pagination, filtersURL);
            this.renderedPagination = this._pagination.renderPagination();
        } else {
            this.renderedPagination = '';
        }
        
        if (!this.paginationEventAdded) {
            await this.paginationClick();
        } else {
            this.updatePaginationButtons(this.pagination?.page || 1);
        }
    }

    private async updateDocumentCards() {
        this.documentCards = '';
        let tagLabelList: BgTagInterface[] = [];
        for (let tag of this.availableFilters) {
            tagLabelList.push(this.getLabelFromHref(this.filters?.filterGroup || [], tag) ?? {label: "", href: "", selectorPrefix: "", disclaimer: ""} );
        }
        for (const card of this.responseData) {
            const tagListJson = tagLabelList.filter(tag => card.tags?.some(item => item.toLowerCase() === tag.value?.toLowerCase()));
            const tagList = JSON.stringify(tagListJson || []);
            const tagLabels = JSON.stringify(card.tagLabels || []);
            this.documentCards += `<bg-document-card documenttype="${card.documentType}" 
                                        tags='${tagList}'
                                        tagLabels='${tagLabels}'
                                        url="${card.url}" 
                                        title="${card.title}" 
                                        filesize="${card.fileSize}">
                        </bg-document-card>`
        }

    }

    private async applySortingEvent() {

        const dropdownMenu = this.shadowRoot?.querySelector('sl-menu');
        if (dropdownMenu) {
            dropdownMenu.addEventListener('sl-select', async (event) => {
                const selectedItem = event.detail.item;
                const sortDiv = selectedItem.querySelector('.bg-advanced-filter-dropdown-selection-item');
                console.log("Selected sort option:", sortDiv?.getAttribute('sort'));
                if (sortDiv && this.totalResults > 0) {
                    this.filterList = await this.setFilterList();
                    this.currentSortOption = sortDiv.getAttribute('sort') || 'desc';
                    this.documents = await this.getDocuments(this.filterList);
                    this.currentSortLabel = this.currentSortOption === 'desc' ? this.sortDescLabel : this.sortAscLabel;
                    await this.updateDocumentCardList(this.filterList, this.documents);
                    await this.updateDocumentCards();
                    await this.updateInfoBoxes()
                    await this.renderNoFilterSelected()
                    const sortMenu = this.shadowRoot?.getElementById('sort-menu');
                    if (sortMenu) {
                        sortMenu.innerText = this.currentSortLabel;
                    }
                }
            });
        }
    }

    private async updateResultText() {
        const sortButton = this.shadowRoot?.querySelector('.bg-advanced-filter-dropdown-selection');
        const currentPage = this.pagination?.page ?? 1;
        let startItem = (currentPage - 1) * this.pageSize + 1;
        let endItem = this.responseData.length + startItem - 1;

        if (this.totalResults === 0) {
            startItem = 0;
            endItem = 0;
        } else if (endItem > this.totalResults) {
            endItem = this.totalResults;
        }

        const text = this.totalResults > 0 ? `Showing ${startItem}-${endItem} of ${this.totalResults} documents` : '';
        const resultText = this.shadowRoot?.getElementById('result-text')
        if (resultText) {
            resultText.textContent = text;
        }

        if (this.totalResults > 0) {
            sortButton?.classList.remove('hidden');
        } else {
            sortButton?.classList.add('hidden');
        }

    }
    
   
    private async updateDocumentInfoButtons() {
        const currentFilterCount = this.filters?.currentFilterLabels?.length ?? 0;
        this.currentSortLabel = this.currentSortOption === 'desc' ? this.sortDescLabel : this.sortAscLabel;
        this.infoButtons = `
        <div id="info-buttons-container" class="bg-advanced-filter-info-container" style="--bg-filter-info-display:flex"
             slot="results-info">
            <div class="bg-advanced-filter-info-container-row">
               <div id="result-text" class="bg-advanced-filter-result-info">
                </div>
                <div class="bg-advanced-filter-info-container-buttons">
                  <slot name="filter-button"></slot>
                    <div class="bg-advanced-filter-dropdown-selection">
                        <label>Sort by: </label>
                        <sl-dropdown>
                            <sl-button id="sort-menu" class="bg-advanced-filter-button bg-advanced-filter-info-button" slot="trigger"
                                       caret>
                                ${this.currentSortLabel}
                            </sl-button>
                            <sl-menu>
                                <sl-menu-item>
                                    <div class="bg-advanced-filter-dropdown-selection-item" sort="desc">${this.sortDescLabel}</div>
                                </sl-menu-item>
                                <sl-menu-item>
                                    <div class="bg-advanced-filter-dropdown-selection-item" sort="asc">${this.sortAscLabel}</div>
                                </sl-menu-item>
                            </sl-menu>
                        </sl-dropdown>
                    </div>
                </div>
            </div>
    
        </div>`

    }

    private async updateInfoBoxes() {
        this.infoBox = '<div class="bg-advanced-filter-disclaimer-container">';
        let tagLabelList: BgTagInterface[] = [];
        for (let tag of this.availableFilters) {
            tagLabelList.push(this.getLabelFromHref(this.filters?.filterGroup || [], tag) ?? {label: tag, href: tag});
        }
        const activeFilter = this.shadowRoot?.querySelectorAll('.bg-advanced-filter-option.active') ?? [];
        for (const info of tagLabelList) {
            const disclaimerText = info.disclaimer?.replace(/<[^>]*>/g, '').trim() ?? '';
            if (disclaimerText !== '' && activeFilter.length > 0) {
                this.infoBox += `
                        <div class="bg-advanced-filter-disclaimer">
                            <i class="icon-info bg-advanced-filter-disclaimer-icon"></i>
                            ${info.disclaimer}
                        </div>`
            }
        }
        this.infoBox += `</div>`
    }

    private setSingleUrlParameter(paramName: string, paramValue: string) {
        const url = new URL(window.location.href);
        if (paramValue === '') {
            url.searchParams.delete(paramName)
        } else {
            url.searchParams.set(paramName, paramValue);
        }
        window.history.pushState(null, '', url.toString());
    }

    private getLabelFromHref(data: FilterGroupInterface[], hrefToFind: string): BgTagInterface | null {
        for (const group of data) {
            const foundFilter = group.filters.find(filter => filter.href.toLowerCase() === hrefToFind.toLowerCase());
            if (foundFilter) {
                return foundFilter;
            }
        }
        return null;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'bg-document-list': BgDocumentList;
    }
}

function getFiltersFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const filter = urlParams.get('filter');
    return filter ? `&filter=${filter}` : '';
}


